// Bottle colour popup for products with metafield custom.min_2_qty = true.
// Opens instead of the regular add to cart, shows one colour picker per bottle (2-6)
// and adds the selected variants to the cart grouped by colour.
if (!customElements.get("bottles-popup")) {
  customElements.define(
    "bottles-popup",
    class BottlesPopup extends HTMLElement {
      connectedCallback() {
        this.formId = this.dataset.formId;
        this.form = document.getElementById(this.formId);
        this.minBottles = parseInt(this.dataset.min) || 2;
        this.maxBottles = parseInt(this.dataset.max) || 6;
        // Fizz Bundle Plus: pickers show Fizz Bottle colours, the bundle variant comes from the PDP form
        this.isBundle = this.dataset.bundle === "true";
        this.grid = this.querySelector(".bottles-popup__grid");
        this.items = Array.from(this.querySelectorAll(".bottles-popup__item"));
        this.submitButton = this.querySelector(".bottles-popup__submit");
        this.errorElement = this.querySelector(".bottles-popup__error");

        this._abortController = new AbortController();
        const { signal } = this._abortController;

        // Openers: any element pointing to this form (PDP add to cart button)
        document
          .querySelectorAll(`[data-bottles-popup-open="${this.formId}"]`)
          .forEach((opener) =>
            opener.addEventListener(
              "click",
              (event) => {
                event.preventDefault();
                this.open(opener);
              },
              { signal }
            )
          );

        // Enter in the quantity field submits the form: open the popup instead of a direct add to cart.
        // Capture phase on the parent runs before the product-form submit handler.
        if (this.form && this.form.parentElement) {
          this.form.parentElement.addEventListener(
            "submit",
            (event) => {
              if (event.target !== this.form) return;
              event.preventDefault();
              event.stopImmediatePropagation();
              this.open();
            },
            { capture: true, signal }
          );
        }

        this.querySelector(".bottles-popup__close").addEventListener("click", () => this.close(), { signal });

        this.addEventListener(
          "click",
          (event) => {
            if (event.target === this) this.close();
          },
          { signal }
        );

        document.addEventListener(
          "keyup",
          (event) => {
            if (event.key === "Escape" && this.classList.contains("active")) this.close();
          },
          { signal }
        );

        // Update bottle image on colour change
        this.addEventListener(
          "change",
          (event) => {
            const input = event.target;
            if (!input.matches('input[type="radio"]')) return;
            const item = input.closest(".bottles-popup__item");
            if (item) item.dataset.touched = "true";
            this.updateItemImage(item, input);
            this.hideError();
          },
          { signal }
        );

        this.submitButton.addEventListener("click", () => this.onSubmit(), { signal });
      }

      disconnectedCallback() {
        this._abortController?.abort();
      }

      getQuantityInput() {
        if (!this.form) return null;
        return (
          document.querySelector(`input[name="quantity"][form="${this.formId}"]`) ||
          this.form.querySelector('[name="quantity"]')
        );
      }

      getBottleCount() {
        const quantityInput = this.getQuantityInput();
        const quantity = parseInt(quantityInput?.value) || this.minBottles;
        const count = Math.min(Math.max(quantity, this.minBottles), this.maxBottles);

        // Keep the PDP quantity field in sync with the clamped value
        if (quantityInput && quantityInput.type !== "hidden" && parseInt(quantityInput.value) !== count) {
          quantityInput.value = count;
        }

        return count;
      }

      getMainVariantId() {
        return this.form?.querySelector('[name="id"]')?.value || null;
      }

      open(opener) {
        this.opener = opener || document.activeElement;
        const count = this.getBottleCount();
        const mainVariantId = this.getMainVariantId();

        this.grid.dataset.count = count;
        this.items.forEach((item, index) => {
          const isVisible = index < count;
          item.hidden = !isVisible;
          if (isVisible) this.preselectItem(item, mainVariantId);
        });

        this.hideError();
        this.classList.add("active");
        document.body.classList.add("model-active");
        this.querySelector(".bottles-popup__modal").focus();
      }

      close() {
        this.classList.remove("active");
        document.body.classList.remove("model-active");
        if (this.opener && typeof this.opener.focus === "function") this.opener.focus();
      }

      // Bottles keep the customer's choice; untouched bottles follow the colour selected on the PDP
      preselectItem(item, mainVariantId) {
        const inputs = Array.from(item.querySelectorAll('input[type="radio"][data-variant-id]'));
        if (!inputs.length) return;

        const checkedInput = inputs.find((input) => input.checked);
        if (item.dataset.touched === "true" && checkedInput) return;

        // Fizz Bundle Plus: bottle variants differ from the bundle variant, so match the PDP colour by name
        const mainColour = this.isBundle ? this.getMainColour() : null;
        const targetInput =
          inputs.find((input) => input.dataset.variantId === String(mainVariantId)) ||
          (mainColour && inputs.find((input) => input.value === mainColour)) ||
          checkedInput ||
          inputs[0];
        targetInput.checked = true;
        this.updateItemImage(item, targetInput);
      }

      // Colour selected on the PDP (variant radios are linked to the main form)
      getMainColour() {
        return document.querySelector(`input[type="radio"][form="${this.formId}"]:checked`)?.value || null;
      }

      updateItemImage(item, input) {
        if (!item || !input) return;
        const label = item.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        const imageUrl = label?.getAttribute("data-variant-img");
        const image = item.querySelector(".product-form__image img");
        if (imageUrl && image) image.src = imageUrl;
      }

      getSelectedItems() {
        const quantities = new Map();
        const visibleItems = this.items.filter((item) => !item.hidden);

        const colours = new Map();

        for (const item of visibleItems) {
          const input = item.querySelector('input[type="radio"]:checked');
          const variantId = input?.dataset.variantId;
          if (!variantId) return null;
          quantities.set(variantId, (quantities.get(variantId) || 0) + 1);
          colours.set(variantId, input.value);
        }

        const properties = this.dataset.itemProperty ? { "What's Included": this.dataset.itemProperty } : undefined;

        // Fizz Bundle Plus: the bundle variant selected on the PDP, one line per bottle colour,
        // the bottle variant id is read by the order-edit app
        if (this.isBundle) {
          const bundleVariantId = this.getMainVariantId();
          if (!bundleVariantId) return null;

          return Array.from(quantities, ([bottleVariantId, quantity]) => ({
            id: Number(bundleVariantId),
            quantity,
            properties: {
              ...properties,
              "_Bottle Color 1": bottleVariantId,
              "Bottle Color 1": colours.get(bottleVariantId),
            },
          }));
        }

        return Array.from(quantities, ([id, quantity]) => {
          const item = { id: Number(id), quantity };
          if (properties) item.properties = properties;
          return item;
        });
      }

      onSubmit() {
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        const items = this.getSelectedItems();
        if (!items || !items.length) {
          this.showError(window.cartStrings?.error || "Please choose a colour for every bottle.");
          return;
        }

        const cart = document.querySelector("cart-drawer");
        const body = { items };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        this.setLoading(true);

        fetch(`${routes.cart_add_url}`, { ...fetchConfig("javascript"), body: JSON.stringify(body) })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message);
              return;
            }

            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, { source: "bottles-popup" });
            this.close();
            // Same as product-form.js: renderContents() only clears .drawer__inner, the drawer itself keeps "is-empty"
            cart.classList.remove("is-empty");
            cart.renderContents(response);
          })
          .catch((error) => {
            console.error(error);
            this.showError(window.cartStrings?.error);
          })
          .finally(() => this.setLoading(false));
      }

      setLoading(isLoading) {
        this.submitButton.classList.toggle("loading", isLoading);
        if (isLoading) {
          this.submitButton.setAttribute("aria-disabled", "true");
        } else {
          this.submitButton.removeAttribute("aria-disabled");
        }
        this.submitButton.querySelector(".loading-overlay__spinner")?.classList.toggle("hidden", !isLoading);
      }

      showError(message) {
        if (!this.errorElement || !message) return;
        this.errorElement.textContent = message;
        this.errorElement.hidden = false;
      }

      hideError() {
        if (!this.errorElement) return;
        this.errorElement.textContent = "";
        this.errorElement.hidden = true;
      }
    }
  );
}